import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getAccountOrders, getVault, requestVaultDownload, updatePreferences, money, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/Motion";
import { Download } from "lucide-react";
import { MyNetworkSection, InnerCircleSection, SupporterSection, MyDimensionsSection, CollectionSection } from "../components/account/Sections";
import DimensionVSection from "../components/account/DimensionVSection";

const TABS = [
  ["music", "MY MUSIC & VAULT"], ["collection", "MY COLLECTION"], ["inner-circle", "INNER CIRCLE"],
  ["network", "MY NETWORK"], ["supporter", "SUPPORTER PROFILE"], ["dimensions", "MY DIMENSIONS"],
  ["dimension-v", "DIMENSION V"], ["orders", "ORDERS"], ["settings", "ACCOUNT"],
];

function VaultAsset({ release, asset }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await requestVaultDownload({ digitalId: release.digitalId, assetId: asset.assetId });
      window.open(res.download_path, "_blank");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between py-4 border-t border-white/5" data-testid={`vault-asset-${asset.assetId}`}>
      <div>
        <p className={`text-sm ${asset.available ? "text-white/85" : "text-white/35"}`}>{asset.label}</p>
        <p className="font-mono text-[9px] tracking-[0.25em] text-white/30 mt-1">{asset.kind.toUpperCase()} · {asset.format}</p>
      </div>
      {asset.available ? (
        <button onClick={download} disabled={busy} data-testid={`vault-download-${asset.assetId}`}
          className="flex items-center gap-2.5 border border-gold/50 text-gold font-mono text-[10px] tracking-[0.25em] px-5 py-2.5 hover:bg-gold hover:text-black transition-colors disabled:opacity-50">
          <Download size={13} /> {busy ? "PREPARING…" : "DOWNLOAD"}
        </button>
      ) : (
        <span className="font-mono text-[9px] tracking-[0.25em] text-white/25 border border-white/10 px-5 py-2.5">AWAITING RELEASE</span>
      )}
    </div>
  );
}

function MusicSection({ vault }) {
  return (
    <div data-testid="my-music">
      <p className="font-mono text-[10px] tracking-[0.3em] text-gold/70 mb-8">OWN THE MUSIC. NOT JUST THE STREAM.</p>
      {vault.length === 0 && (
        <div className="border-t border-white/10 py-12" data-testid="vault-empty">
          <p className="font-serif text-2xl text-white/40">Your vault is empty.</p>
          <Link to="/store" data-testid="vault-browse-store" className="inline-block mt-5 font-mono text-[11px] tracking-[0.25em] text-gold hover:text-white transition-colors">BROWSE THE STORE →</Link>
        </div>
      )}
      {vault.map((rel) => (
        <div key={rel.digitalId} className="border-t border-white/10 py-10" data-testid={`vault-release-${rel.digitalId}`}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-3">
              <img src={rel.artwork} alt={rel.title} className="w-full max-w-[220px] aspect-square object-cover" />
            </div>
            <div className="md:col-span-9">
              <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-2">{rel.type.toUpperCase()} · DIMENSION {rel.dimension || "—"}</p>
              <h3 className="font-serif text-3xl font-light text-white">{rel.title}</h3>
              <p className="font-mono text-[9px] tracking-[0.2em] text-white/30 mt-2">{rel.orderNumber} · ACQUIRED {rel.acquiredAt ? new Date(rel.acquiredAt).toLocaleDateString() : ""}</p>
              <div className="mt-6">
                {rel.assets.map((a) => <VaultAsset key={a.assetId} release={rel} asset={a} />)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function OrdersSection({ orders, loaded }) {
  return (
    <div data-testid="section-orders">
      {loaded && orders.length === 0 && <p className="font-serif text-2xl text-white/40 py-8 border-t border-white/10">No orders yet. The universe awaits.</p>}
      {orders.map((o) => (
        <div key={o.order_number} className="border-t border-white/10 py-7" data-testid={`order-${o.order_number}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-4 mb-4">
            <span className="font-mono text-[11px] tracking-[0.2em] text-gold">{o.order_number}</span>
            <span className="font-mono text-[10px] text-white/40">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}</span>
          </div>
          {o.items.map((it, idx) => (
            <div key={idx} className="flex items-baseline justify-between py-1.5">
              <span className="font-serif text-lg text-white">{it.name} <span className="font-mono text-[9px] text-white/40 ml-2">×{it.quantity}</span></span>
              <span className="font-mono text-sm text-white/70">{money(it.price * it.quantity, o.currency)}</span>
            </div>
          ))}
          <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-white/5">
            <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">TOTAL</span>
            <span className="font-serif text-xl text-gold">{money(o.amount, o.currency)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsSection({ user, logout }) {
  const [enabled, setEnabled] = useState(user.vault_notifications !== false);
  const toggle = async () => {
    try {
      const res = await updatePreferences({ vault_notifications: !enabled });
      setEnabled(res.vault_notifications);
      toast.success(res.vault_notifications ? "Vault notifications on." : "Vault notifications off.");
    } catch { toast.error("Could not update preferences."); }
  };
  return (
    <div data-testid="section-settings">
      <div className="border-t border-white/10 py-6 flex items-center justify-between">
        <div>
          <p className="text-white/85">Vault notifications</p>
          <p className="text-white/40 text-sm mt-1">Email me when a new asset enters my vault.</p>
        </div>
        <button onClick={toggle} data-testid="pref-vault-notifications" aria-pressed={enabled}
          className={`font-mono text-[10px] tracking-[0.25em] px-6 py-3 border transition-colors ${
            enabled ? "border-gold/60 text-gold" : "border-white/20 text-white/40"}`}>
          {enabled ? "ON" : "OFF"}
        </button>
      </div>
      <div className="border-t border-white/10 py-6 flex items-center justify-between">
        <div>
          <p className="text-white/85">Email verification</p>
          <p className="text-white/40 text-sm mt-1">{user.emailVerified ? "Your email is verified." : "Not yet verified — check your inbox."}</p>
        </div>
        <span className={`font-mono text-[9px] tracking-[0.25em] ${user.emailVerified ? "text-gold" : "text-white/40"}`}>
          {user.emailVerified ? "VERIFIED" : "PENDING"}
        </span>
      </div>
      <div className="border-t border-white/10 py-6 flex items-center justify-between">
        <div>
          <p className="text-white/85">Password</p>
          <p className="text-white/40 text-sm mt-1">Reset via a secure email link.</p>
        </div>
        <Link to="/forgot-password" className="font-mono text-[10px] tracking-[0.25em] text-white/50 hover:text-gold">RESET →</Link>
      </div>
      <button onClick={logout} data-testid="account-logout"
        className="mt-10 border border-white/30 font-mono text-[11px] tracking-[0.25em] px-8 py-3.5 hover:border-gold hover:text-gold transition-colors">
        SIGN OUT
      </button>
    </div>
  );
}

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("section") || "music";
  const [orders, setOrders] = useState([]);
  const [vault, setVault] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { document.title = "Your Account — Motion & Meaning"; }, []);
  useEffect(() => {
    if (user === false) navigate("/login?next=/account", { replace: true });
    if (user) {
      getAccountOrders().then(setOrders).catch(() => {}).finally(() => setLoaded(true));
      getVault().then((v) => setVault(v.releases || [])).catch(() => {});
    }
  }, [user, navigate]);
  useEffect(() => {
    if (params.get("membership") === "success") setParams({ section: "inner-circle", membership: "success", session_id: params.get("session_id") || "" }, { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return <div className="min-h-screen" />;

  return (
    <div className="pt-32 md:pt-44 pb-32 min-h-screen" data-testid="account-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-5">LISTEN → OWN → JOIN → SHARE → EARN → COLLECT → UNLOCK</p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5">
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-none text-white">{user.name || "Fan"}</h1>
            <span className="font-mono text-[11px] tracking-[0.15em] text-white/40">{user.email}</span>
          </div>
        </Reveal>

        <div className="flex gap-2 overflow-x-auto mt-12 pb-2 border-b border-white/10">
          {TABS.map(([id, l]) => (
            <button key={id} onClick={() => setParams({ section: id })} data-testid={`account-tab-${id}`}
              className={`whitespace-nowrap font-mono text-[9px] tracking-[0.2em] px-4 py-2.5 border transition-colors ${
                tab === id ? "border-gold text-gold" : "border-white/10 text-white/45 hover:text-white/80"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="mt-12">
          {tab === "music" && <MusicSection vault={vault} />}
          {tab === "collection" && <CollectionSection orders={orders} />}
          {tab === "inner-circle" && <InnerCircleSection />}
          {tab === "network" && <MyNetworkSection user={user} />}
          {tab === "supporter" && <SupporterSection />}
          {tab === "dimensions" && <MyDimensionsSection />}
          {tab === "dimension-v" && <DimensionVSection refreshUser={refresh} />}
          {tab === "orders" && <OrdersSection orders={orders} loaded={loaded} />}
          {tab === "settings" && <SettingsSection user={user} logout={logout} />}
        </div>
      </div>
    </div>
  );
}
